'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

const ROLES = [
  {
    id: 'fish_farmer',
    label: 'Fish Farmer',
    description: 'Pond management, disease control, yield optimization',
    icon: 'HomeModernIcon',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-400',
    activeBg: 'bg-emerald-500',
  },
  {
    id: 'student',
    label: 'Student',
    description: 'Scientific foundations, research, fisheries science',
    icon: 'AcademicCapIcon',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-400',
    activeBg: 'bg-blue-500',
  },
  {
    id: 'entrepreneur',
    label: 'Entrepreneur',
    description: 'Business planning, market access, scaling ventures',
    icon: 'RocketLaunchIcon',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-400',
    activeBg: 'bg-orange-500',
  },
  {
    id: 'technician',
    label: 'Technician',
    description: 'Equipment, feed formulation, water testing',
    icon: 'WrenchScrewdriverIcon',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-400',
    activeBg: 'bg-purple-500',
  },
];

export default function SignupPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) {
      setError('Please select your role to continue.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await signUp(email, password, { fullName, role: selectedRole });
      router.push('/personalized-dashboard');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-secondary to-ocean-teal flex-col justify-between p-12 text-white">
        <Link href="/" className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12 12-5.373 12-12S20.627 2 14 2zm0 3c1.657 0 3 1.343 3 3s-1.343 3-3 3-3-1.343-3-3 1.343-3 3-3zm0 16c-3.314 0-6-2.686-6-6 0-.768.156-1.5.438-2.172C9.844 13.594 11.782 14 14 14s4.156-.406 5.562-1.172c.282.672.438 1.404.438 2.172 0 3.314-2.686 6-6 6z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <span className="text-xl font-headline font-bold">FISCMAP</span>
            <span className="text-xs text-white/70 block">Portal</span>
          </div>
        </Link>

        <div>
          <h1 className="text-4xl font-headline font-bold mb-4 leading-tight">
            Join 45,000+<br />Fisheries Professionals
          </h1>
          <p className="text-white/80 text-lg mb-8">
            Get a personalized learning experience tailored to your role — whether you&apos;re a farmer, student, entrepreneur, or technician.
          </p>
          <div className="space-y-3">
            {[
              { icon: 'CheckCircleIcon', text: 'Role-based personalized dashboard' },
              { icon: 'CheckCircleIcon', text: 'Track course progress & earn certificates' },
              { icon: 'CheckCircleIcon', text: 'Access market opportunities & buyers' },
              { icon: 'CheckCircleIcon', text: 'AI-powered learning recommendations' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <Icon name={item.icon as any} size={20} variant="solid" className="text-accent flex-shrink-0" />
                <span className="text-white/85 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/50 text-sm">© 2026 FISCMAP Portal. All rights reserved.</p>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12 12-5.373 12-12S20.627 2 14 2zm0 3c1.657 0 3 1.343 3 3s-1.343 3-3 3-3-1.343-3-3 1.343-3 3-3zm0 16c-3.314 0-6-2.686-6-6 0-.768.156-1.5.438-2.172C9.844 13.594 11.782 14 14 14s4.156-.406 5.562-1.172c.282.672.438 1.404.438 2.172 0 3.314-2.686 6-6 6z" fill="white"/>
              </svg>
            </div>
            <span className="text-xl font-headline font-bold text-foreground">FISCMAP Portal</span>
          </div>

          <h2 className="text-3xl font-headline font-bold text-foreground mb-2">Create account</h2>
          <p className="text-muted-foreground mb-6">
            Already have an account?{' '}
            <Link href="/login" className="text-primary font-semibold hover:underline">
              Sign in
            </Link>
          </p>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <Icon name="ExclamationCircleIcon" size={18} variant="solid" className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Full name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Your full name"
                className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Min. 6 characters"
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} variant="outline" />
                </button>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">I am a... <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 gap-3">
                {ROLES.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setSelectedRole(role.id)}
                    className={`p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                      selectedRole === role.id
                        ? `${role.bgColor} ${role.borderColor} shadow-sm`
                        : 'border-border bg-card hover:border-muted-foreground/30'
                    }`}
                  >
                    <div className={`flex items-center gap-2 mb-1 ${selectedRole === role.id ? role.color : 'text-foreground'}`}>
                      <Icon name={role.icon as any} size={18} variant={selectedRole === role.id ? 'solid' : 'outline'} />
                      <span className="font-semibold text-sm">{role.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-tight">{role.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-headline font-semibold hover:bg-primary/90 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating account...
                </>
              ) : (
                'Create free account'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-6">
            By creating an account, you agree to our{' '}
            <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}
